import { useEffect } from 'react';
import { Minus, X } from 'lucide-react';
import { AgentThinkingComic } from '@/webmcp/ui/AgentThinkingComic';
import { CentralChatPanelBody } from './CentralChatPanelBody';
import { CALLOUT_UNREAD_TEXT, calloutTextFor } from './badgerCallout';
import { useCentralChatRuntime } from './centralChatRuntime';
import { EmjaeMascot } from './EmjaeMascot';
import { useBadgerCallout } from './useBadgerCallout';
import { useNarrowScreen } from './useNarrowScreen';

/**
 * The floating Emjae bubble: the corner badger is the launcher, and it opens a
 * floating window (full screen under `md`). It is the DEFAULT chat surface
 * (`VITE_CHAT_SURFACE` unset or anything but `rail`), mounted by CentralChatSurface
 * on every page except the editor and /chat. With `VITE_CHAT_SURFACE=rail` it is
 * the fallback only on routes that have no rail (settings); there the layout
 * renders CentralChatRail on the other pages, so the two never appear together.
 * Both surfaces share CentralChatPanelBody; the bubble only owns its launcher, the
 * call-out beside it and its fixed open panel chrome.
 *
 * The call-out (speech bubble) shows: once per browser session shortly after the
 * first load, with a wave; on hover or keyboard focus of the badger; and, every
 * time, when a reply finishes while the window is closed.
 */
export function CentralChatFloatingBubble({ pathname = '/' }: { pathname?: string }) {
  const chat = useCentralChatRuntime();
  const narrow = useNarrowScreen();
  const { bubbleOpen, unreadCount } = chat;
  const callout = useBadgerCallout({ bubbleOpen, unreadCount });

  // The full-screen window (under md) is modal: keep the page behind it from
  // scrolling while it is open, and put the old value back when it closes.
  useEffect(() => {
    if (!bubbleOpen || !narrow) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [bubbleOpen, narrow]);

  if (chat.isFullChatRoute) return null;

  if (!bubbleOpen) {
    const { calloutId, calloutVisible, live, wave, hop, dismiss } = callout;
    const text = callout.unreadNote ? CALLOUT_UNREAD_TEXT : calloutTextFor(pathname);
    return (
      <div
        className="fixed bottom-5 right-5 z-40 flex items-end gap-3"
        onFocus={callout.onFocusWithin}
        onBlur={callout.onBlurWithin}
      >
        <AgentThinkingComic
          variant="collapsed"
          className="absolute bottom-[calc(100%+0.5rem)] right-0"
        />
        {calloutVisible && (
          <div
            id={calloutId}
            data-testid="badger-callout"
            role={live ? 'status' : undefined}
            className="absolute bottom-3 right-[calc(100%+0.25rem)] flex max-w-[min(220px,calc(100vw-6rem))] items-start gap-1.5 rounded-xl border border-warm-border bg-paper py-2 pl-3 pr-1.5 shadow-lg"
          >
            <span className="min-w-0 break-words text-[13px] leading-snug text-ink">
              {text}
            </span>
            <button
              type="button"
              data-testid="badger-callout-dismiss"
              aria-label="Dismiss"
              onClick={dismiss}
              className="grid size-5 shrink-0 place-items-center rounded-full text-ink-3 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-3" aria-hidden="true" />
            </button>
            {/* The tail, pointing at the badger. */}
            <span
              aria-hidden="true"
              className="absolute -right-1.5 bottom-3 size-3 rotate-45 border-r border-t border-warm-border bg-paper"
            />
          </div>
        )}
        <button
          type="button"
          data-testid="badger-launcher"
          aria-label={unreadCount > 0 ? `Open Emjae, ${unreadCount} unread` : 'Open Emjae'}
          aria-describedby={calloutVisible && !live ? calloutId : undefined}
          onClick={chat.openBubble}
          onMouseEnter={() => callout.setHover(true)}
          onMouseLeave={() => callout.setHover(false)}
          className="group relative grid place-items-center rounded-full transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
        >
          <EmjaeMascot
            size="launcher"
            px={narrow ? 52 : 68}
            pose={hop ? 'happy' : wave ? 'wave' : 'idle'}
            motion={[!wave && !hop && 'float', hop && 'bounce', wave && 'wave']}
            onAnimationEnd={callout.onMascotAnimationEnd}
          />
          {unreadCount > 0 && (
            <span
              data-testid="badger-unread"
              className="absolute -right-1 -top-1 grid min-w-5 place-items-center rounded-full bg-primary px-1.5 py-0.5 font-mono text-[10px] font-semibold leading-none text-primary-foreground ring-2 ring-white"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </div>
    );
  }

  const chromeActions = (
    <>
      <button
        type="button"
        onClick={chat.closeBubble}
        title="Collapse chat"
        className="grid size-8 place-items-center rounded-md text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
      >
        <Minus className="size-4" />
      </button>
      <button
        type="button"
        onClick={chat.closeBubble}
        title="Close chat"
        className="grid size-8 place-items-center rounded-md text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
      >
        <X className="size-4" />
      </button>
    </>
  );

  return (
    <section
      aria-label="Emjae chat"
      // Under md the window is full screen and modal: a dialog, so the page behind
      // it is out of the reading order.
      {...(narrow ? { role: 'dialog', 'aria-modal': true } : {})}
      className={
        narrow
          ? 'fixed inset-0 z-40 flex flex-col overflow-hidden overscroll-contain bg-warm-bg pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] text-ink'
          : 'fixed bottom-5 right-5 z-40 flex h-[min(72vh,620px)] w-[min(calc(100vw-2rem),420px)] flex-col overflow-hidden rounded-xl border border-warm-border bg-warm-bg text-ink shadow-2xl shadow-black/20'
      }
    >
      <CentralChatPanelBody chromeActions={chromeActions} onLeave={chat.closeBubble} />
    </section>
  );
}
